import { useState, useCallback, useMemo } from "react";
import { Flag, AlertTriangle, Settings2, ShieldAlert } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import {
  useFeatureFlags,
  useUpdateFeatureFlag,
} from "@/hooks/admin/useEnterprise";
import type {
  FeatureFlag,
  FlagEnvironment,
  UpdateFeatureFlagInput,
} from "@/lib/admin-api/enterprise";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ENV_LABELS: Record<FlagEnvironment, string> = {
  production: "Production",
  staging: "Staging",
  all: "Tous",
};

const ENV_COLORS: Record<FlagEnvironment, string> = {
  production: "bg-red-100 text-red-800 border-red-200",
  staging: "bg-blue-100 text-blue-800 border-blue-200",
  all: "bg-stone-100 text-stone-700 border-stone-200",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// Edit Flag Modal
// ---------------------------------------------------------------------------

interface EditFlagModalProps {
  readonly flag: FeatureFlag | null;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (id: string, input: UpdateFeatureFlagInput) => Promise<void>;
}

function EditFlagModal({ flag, onClose, isSaving, onSave }: EditFlagModalProps) {
  const [rollout, setRollout] = useState(flag?.rollout_percent ?? 0);
  const [env, setEnv] = useState<FlagEnvironment>(flag?.environment ?? "all");
  const [enabled, setEnabled] = useState(flag?.enabled ?? false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!flag) return;
    setError(null);
    try {
      await onSave(flag.id, {
        rollout_percent: rollout,
        environment: env,
        enabled,
      });
      toast.success(`Indicateur "${flag.name}" mis a jour`);
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Une erreur est survenue.";
      setError(message);
      toast.error("Echec de la mise a jour");
    }
  }, [flag, rollout, env, enabled, onSave, handleClose]);

  if (!flag) return null;

  // Rollout bar color based on percentage
  const barColor =
    rollout === 0
      ? "bg-stone-300"
      : rollout < 25
      ? "bg-blue-400"
      : rollout < 75
      ? "bg-amber-500"
      : "bg-emerald-500";

  return (
    <AdminModal
      open={Boolean(flag)}
      onClose={handleClose}
      title={`Modifier : ${flag.name}`}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Enregistrement..." : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {error && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 px-3 py-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Description */}
        {flag.description && (
          <div className="rounded-lg bg-stone-50 px-4 py-3">
            <p className="text-xs font-medium text-stone-500">Description</p>
            <p className="mt-0.5 text-sm text-stone-700">{flag.description}</p>
          </div>
        )}

        {/* Enabled toggle */}
        <div className="flex items-center justify-between rounded-lg border border-stone-200 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-stone-900">Activer la fonctionnalite</p>
            <p className="text-xs text-stone-500">
              {enabled ? "La fonctionnalite est active" : "La fonctionnalite est desactivee"}
            </p>
          </div>
          <ToggleSwitch
            checked={enabled}
            onChange={setEnabled}
          />
        </div>

        {/* Rollout slider */}
        <div>
          <label className="mb-3 flex items-center justify-between text-sm font-medium text-stone-700">
            <span>Deploiement progressif</span>
            <span className="text-lg font-bold text-stone-900">{rollout} %</span>
          </label>
          <div className="relative">
            <input
              type="range"
              min={0}
              max={100}
              value={rollout}
              onChange={(e) => setRollout(Number(e.target.value))}
              className="w-full accent-amber-600"
            />
            {/* Visual bar */}
            <div className="mt-2 h-2.5 w-full rounded-full bg-stone-200">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${barColor}`}
                style={{ width: `${rollout}%` }}
              />
            </div>
          </div>
          <div className="mt-1 flex justify-between text-xs text-stone-400">
            <span>0 %</span>
            <span>25 %</span>
            <span>50 %</span>
            <span>75 %</span>
            <span>100 %</span>
          </div>
        </div>

        {/* Environment */}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-stone-700">
            Environnement
          </label>
          <div className="grid grid-cols-3 gap-2">
            {(Object.entries(ENV_LABELS) as [FlagEnvironment, string][]).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setEnv(value)}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                  env === value
                    ? ENV_COLORS[value]
                    : "border-stone-200 text-stone-600 hover:bg-stone-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Meta info */}
        <div className="grid grid-cols-2 gap-3 rounded-lg bg-stone-50 p-3 text-xs text-stone-500">
          <div>
            <p className="font-medium text-stone-400">Cle</p>
            <code className="text-stone-700">{flag.key}</code>
          </div>
          <div>
            <p className="font-medium text-stone-400">Derniere modification</p>
            <p className="text-stone-700">{formatDate(flag.updated_at)} par {flag.updated_by}</p>
          </div>
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Toggle switch component
// ---------------------------------------------------------------------------

interface ToggleSwitchProps {
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
  readonly disabled?: boolean;
}

function ToggleSwitch({ checked, onChange, disabled = false }: ToggleSwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-amber-600" : "bg-stone-200"
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-1"
        }`}
      />
    </button>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function FeatureFlags() {
  useAdminPageTitle("Indicateurs de fonctionnalite");

  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);

  const { data, isLoading, isError } = useFeatureFlags();
  const updateMutation = useUpdateFeatureFlag();

  const flags = data?.flags ?? [];

  const enabledCount = useMemo(() => flags.filter((f) => f.enabled).length, [flags]);
  const disabledCount = useMemo(() => flags.filter((f) => !f.enabled).length, [flags]);
  const fullRollout = useMemo(
    () => flags.filter((f) => f.enabled && f.rollout_percent === 100).length,
    [flags],
  );
  const partialRollout = useMemo(
    () => flags.filter((f) => f.enabled && f.rollout_percent > 0 && f.rollout_percent < 100).length,
    [flags],
  );

  const handleToggle = useCallback(
    async (flag: FeatureFlag, enabled: boolean) => {
      try {
        await updateMutation.mutateAsync({ id: flag.id, input: { enabled } });
        toast.success(
          enabled
            ? `"${flag.name}" active`
            : `"${flag.name}" desactive`,
        );
      } catch {
        toast.error("Echec de la mise a jour");
      }
    },
    [updateMutation],
  );

  const handleUpdate = useCallback(
    async (id: string, input: UpdateFeatureFlagInput) => {
      await updateMutation.mutateAsync({ id, input });
    },
    [updateMutation],
  );

  const columns: readonly ColumnDef<FeatureFlag>[] = [
    {
      id: "key",
      header: "Cle",
      cell: (row) => (
        <code className="rounded bg-stone-100 px-2 py-0.5 font-mono text-xs text-stone-700">
          {row.key}
        </code>
      ),
    },
    {
      id: "name",
      header: "Nom",
      cell: (row) => (
        <div>
          <p className="text-sm font-medium text-stone-900">{row.name}</p>
          {row.description && (
            <p className="mt-0.5 max-w-xs truncate text-xs text-stone-500">{row.description}</p>
          )}
        </div>
      ),
    },
    {
      id: "enabled",
      header: "Actif",
      cell: (row) => (
        <ToggleSwitch
          checked={row.enabled}
          onChange={(val) => handleToggle(row, val)}
          disabled={updateMutation.isPending}
        />
      ),
    },
    {
      id: "rollout_percent",
      header: "Deploiement",
      cell: (row) => {
        const barColor =
          row.rollout_percent === 0
            ? "bg-stone-300"
            : row.rollout_percent < 25
            ? "bg-blue-400"
            : row.rollout_percent < 75
            ? "bg-amber-500"
            : "bg-emerald-500";
        return (
          <div className="flex items-center gap-2">
            <div className="h-2 w-24 rounded-full bg-stone-200">
              <div
                className={`h-2 rounded-full transition-all ${barColor}`}
                style={{ width: `${row.rollout_percent}%` }}
              />
            </div>
            <span className="min-w-[3rem] text-right text-xs font-medium text-stone-700">
              {row.rollout_percent} %
            </span>
          </div>
        );
      },
    },
    {
      id: "environment",
      header: "Environnement",
      cell: (row) => (
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${ENV_COLORS[row.environment]}`}
        >
          {ENV_LABELS[row.environment]}
        </span>
      ),
    },
    {
      id: "updated_at",
      header: "Modifie le",
      cell: (row) => (
        <div>
          <span className="text-xs text-stone-500">{formatDate(row.updated_at)}</span>
          <p className="text-xs text-stone-400">par {row.updated_by}</p>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row) => (
        <button
          onClick={() => setEditingFlag(row)}
          className="flex items-center gap-1 rounded-md border border-stone-200 px-2.5 py-1 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-50"
        >
          <Settings2 className="h-3 w-3" />
          Modifier
        </button>
      ),
    },
  ];

  if (isError) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <ShieldAlert className="h-10 w-10 text-red-400" />
          <p className="mt-3 text-sm font-medium text-stone-700">
            Erreur lors du chargement des indicateurs
          </p>
          <p className="mt-1 text-xs text-stone-500">
            Veuillez reessayer ou contacter le support.
          </p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <Flag className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              Indicateurs de fonctionnalite
            </h1>
            <p className="text-sm text-stone-500">
              Controle du deploiement progressif des fonctionnalites systeme
            </p>
          </div>
        </div>

        {/* Warning banner */}
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Attention</span> \u2014 Ces indicateurs
            affectent le comportement du systeme en production. Toute modification est
            appliquee immediatement.
          </p>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard label="Actives" value={enabledCount} icon={<Flag className="h-4 w-4" />} />
          <KPICard label="Desactivees" value={disabledCount} />
          <KPICard label="Deploiement complet" value={fullRollout} />
          <KPICard label="Deploiement partiel" value={partialRollout} />
        </div>

        {/* Table */}
        <AdminDataTable
          columns={columns}
          data={flags}
          isLoading={isLoading}
          emptyMessage="Aucun indicateur de fonctionnalite configure."
        />
      </div>

      <EditFlagModal
        flag={editingFlag}
        onClose={() => setEditingFlag(null)}
        isSaving={updateMutation.isPending}
        onSave={handleUpdate}
      />
    </AdminLayout>
  );
}
